import { useState } from 'react';
import { connect } from 'react-redux';
import { Image, Text } from '@belong/ui';
import {
  ApplicationPetModel,
  KidModel,
  ListingApplicationApplicantModel,
} from 'api/models';
import { startCase } from 'es-toolkit';
import { selectUserProfiles } from 'store/redux/renter-accounts/selectors';

type Props = {
  applicants: ListingApplicationApplicantModel[];
  userProfiles: Record<string, any>;
  kids: KidModel[];
  pets: ApplicationPetModel[];
  currentListingApplicant: ListingApplicationApplicantModel;
};

function getApplicantStatusLabelAndColor(
  status: 'Invited' | 'Started' | 'Submitted' | 'VerificationsCompleted',
) {
  switch (status) {
    case 'Invited':
      return { label: 'Not Started', color: 'red' };
    case 'Started':
      return { label: 'Started', color: 'orange' };
    case 'Submitted':
    case 'VerificationsCompleted':
      return { label: 'Completed', color: 'green' };
    default:
      return { label: startCase(status), color: 'red' };
  }
}

const Applicant = ({
  applicant,
  userProfiles,
  currentListingApplicant,
}: {
  applicant: ListingApplicationApplicantModel;
  userProfiles: Record<string, any>;
  currentListingApplicant: ListingApplicationApplicantModel;
}) => {
  const [hasError, setHasError] = useState(false);
  // `residentUniqueId` is optional on the model but this lookup was already
  // unconditional, and every read of the result below goes through `?.`.
  const userProfile = userProfiles[applicant.residentUniqueId!];

  const { label, color } = getApplicantStatusLabelAndColor(applicant.status);

  return (
    <div className="flex flex-row gap-sm">
      <div
        className={
          `relative overflow-hidden rounded-full bg-green` +
          (userProfile?.profileImageUrl
            ? ' border-2 border-solid border-navy'
            : '')
        }
        style={{ width: 50, height: 50 }}
      >
        {!hasError && userProfile?.profileImageUrl && (
          <Image
            src={userProfile.profileImageUrl}
            alt=""
            className="object-cover"
            onError={() => setHasError(true)}
          />
        )}
        {(hasError || !userProfile?.profileImageUrl) && (
          <Image src="/icons/profile_img.svg" alt="" className="object-cover" />
        )}
      </div>
      <div className="flex min-w-0  flex-col">
        <Text className="text-p3 w-[55vw] truncate font-semibold md:w-[195px]">
          {applicant.uniqueId === currentListingApplicant.uniqueId
            ? 'You'
            : userProfile
              ? `${userProfile.firstName} ${userProfile.lastName}`
              : `Applicant ${applicant.applicantType}`}
        </Text>
        <Text className={`text- text-p1 font-semibold${color}`}>{label}</Text>
      </div>
    </div>
  );
};

const Kid = ({ kid }: { kid: KidModel }) => {
  let age: number | null = null;
  if (kid.dateOfBirth) {
    const birthDate = new Date(kid.dateOfBirth);
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      years--;
    }
    age = years;
  }

  return (
    <div className="flex flex-row gap-sm">
      <div
        className="relative overflow-hidden rounded-full"
        style={{ width: 50, height: 50 }}
      >
        <Image
          src="/better-renewals/profile_kids.svg"
          alt=""
          className="object-cover"
        />
      </div>
      <div className="flex flex-col">
        <Text className="text-p3 w-[55vw]  truncate  font-semibold md:w-[195px]">
          {kid.name || 'Kid'}
        </Text>
        {kid.dateOfBirth !== undefined && (
          <Text className="text-p1 text-navy">
            {age} {age !== null && age > 1 ? 'Years' : 'Year'}
          </Text>
        )}
      </div>
    </div>
  );
};

const Pet = ({ pet }: { pet: ApplicationPetModel }) => {
  // `pet` is optional on the model but every read below was already
  // unconditional; the assertion keeps that throw rather than turning it into
  // a silently-empty tile.
  const details = pet.pet!;

  return (
    <div className="flex flex-row gap-sm">
      <div
        className="relative overflow-hidden rounded-full bg-orange"
        style={{ width: 50, height: 50 }}
      >
        {details.petType === 'Dog' && (
          <Image
            src="/better-renewals/profile_dog.svg"
            alt=""
            className="object-cover"
          />
        )}
        {details.petType === 'Cat' && (
          <Image
            src="/better-renewals/profile_cat.svg"
            alt=""
            className="object-cover"
          />
        )}
        {details.petType === 'Other' && (
          <Image
            src="/better-renewals/profile_pet_other.svg"
            alt=""
            className="object-cover"
          />
        )}
      </div>
      <div className="flex flex-col">
        <Text className="text-p3 w-[55vw] truncate  font-semibold md:w-[195px]">
          {details.name ? details.name : 'Pet'}
        </Text>
        {details.subType && (
          <Text className="text-p1 text-navy">{details.subType}</Text>
        )}
      </div>
    </div>
  );
};

const ApplicationApplicantList = ({
  applicants,
  userProfiles,
  kids = [],
  pets = [],
  currentListingApplicant,
}: Props) => {
  return (
    <div className="grid w-full grid-cols-1 gap-sm pt-sm md:grid-cols-2 xl:grid-cols-3">
      {applicants.map((applicant) => (
        <Applicant
          key={applicant.uniqueId}
          applicant={applicant}
          userProfiles={userProfiles}
          currentListingApplicant={currentListingApplicant}
        />
      ))}
      {kids.map((kid) => (
        <Kid key={kid.uniqueId} kid={kid} />
      ))}
      {pets.map((pet) => (
        <Pet key={pet.uniqueId} pet={pet} />
      ))}
    </div>
  );
};

function mapStateToProps(state: any) {
  return {
    userProfiles: selectUserProfiles(state),
  };
}

export default connect(mapStateToProps)(ApplicationApplicantList);
